import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { LifeBuoy, Paperclip, X, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useSupport } from "../contexts/SupportContext";

const MAX_MB = 2;
const MODULES = [
  { value: "cagnottes", label: "Cagnottes" },
  { value: "tontines", label: "Tontines" },
  { value: "paiement", label: "Paiement / Reversement" },
  { value: "compte", label: "Compte / Connexion" },
  { value: "autre", label: "Autre" },
];

function inferModuleFromUrl(url) {
  if (!url) return "autre";
  if (url.includes("/cagnotte")) return "cagnottes";
  if (url.includes("/tontine")) return "tontines";
  if (url.includes("/profile") || url.includes("/login") || url.includes("/register")) return "compte";
  return "autre";
}

export default function SupportDialog() {
  const { open, prefill, closeSupport } = useSupport();
  const { user } = useAuth();
  const [form, setForm] = useState({ module: "autre", subject: "", message: "", reporter_name: "", reporter_email: "", reporter_phone: "" });
  const [attachment, setAttachment] = useState(null); // { filename, content_type, size, data_base64 }
  const [submitting, setSubmitting] = useState(false);

  // Initialize when opening
  useEffect(() => {
    if (!open) return;
    const currentUrl = typeof window !== "undefined" ? window.location.href : "";
    setForm({
      module: prefill.module || inferModuleFromUrl(currentUrl),
      subject: prefill.subject || "",
      message: prefill.message || "",
      reporter_name: user?.name || "",
      reporter_email: user?.email || "",
      reporter_phone: user?.phone || "",
    });
    setAttachment(null);
  }, [open, prefill, user]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (max ${MAX_MB} MB)`);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result || "").split(",")[1];
      setAttachment({ filename: file.name, content_type: file.type || "application/octet-stream", size: file.size, data_base64: b64 });
    };
    reader.onerror = () => toast.error("Impossible de lire le fichier");
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.subject.trim() || form.subject.trim().length < 3) return toast.error("Sujet requis (min 3 caractères)");
    if (!form.message.trim() || form.message.trim().length < 5) return toast.error("Message requis (min 5 caractères)");
    if (!form.reporter_name.trim()) return toast.error("Votre nom est requis");
    if (!form.reporter_email.trim()) return toast.error("Votre email est requis");
    setSubmitting(true);
    try {
      const payload = {
        module: form.module,
        subject: form.subject.trim(),
        message: form.message.trim(),
        reporter_name: form.reporter_name.trim(),
        reporter_email: form.reporter_email.trim().toLowerCase(),
        reporter_phone: form.reporter_phone.trim() || undefined,
        target_id: prefill.target_id || undefined,
        target_url: prefill.target_url || (typeof window !== "undefined" ? window.location.href : undefined),
        error_message: prefill.error_message || undefined,
        error_code: prefill.error_code || undefined,
        attachment: attachment || undefined,
      };
      const { data } = await api.post("/support/tickets", payload);
      toast.success(`Ticket envoyé : ${data.ticket_number}. Nous revenons vers vous rapidement.`);
      closeSupport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeSupport(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-primary" /> Contacter le support</DialogTitle>
          <DialogDescription>
            Notre équipe vous répond sous 24h ouvrées. Décrivez précisément votre incident pour un traitement plus rapide.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {(prefill.error_message || prefill.error_code) && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-xs">
              <div className="font-semibold text-rose-800 dark:text-rose-300 mb-1">Contexte technique joint :</div>
              {prefill.error_code && <div className="font-mono">{prefill.error_code}</div>}
              {prefill.error_message && <div className="text-rose-700 dark:text-rose-300/80">{prefill.error_message}</div>}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Module concerné *</Label>
              <select value={form.module} onChange={(e) => setForm((f) => ({ ...f, module: e.target.value }))} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="support-module">
                {MODULES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Sujet *</Label>
              <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Ex : Ma contribution nest pas comptabilisée" className="mt-1" data-testid="support-subject" />
            </div>
            <div>
              <Label className="text-xs">Nom & prénom *</Label>
              <Input value={form.reporter_name} onChange={(e) => setForm((f) => ({ ...f, reporter_name: e.target.value }))} className="mt-1" data-testid="support-name" />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.reporter_email} onChange={(e) => setForm((f) => ({ ...f, reporter_email: e.target.value }))} className="mt-1" data-testid="support-email" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Téléphone (optionnel)</Label>
              <Input type="tel" value={form.reporter_phone} onChange={(e) => setForm((f) => ({ ...f, reporter_phone: e.target.value }))} placeholder="+221771234567" className="mt-1" data-testid="support-phone" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Message *</Label>
            <Textarea rows={5} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="Décrivez précisément le problème : ce que vous essayiez de faire, ce qui se passe, montants concernés, numéros de téléphone utilisés…" className="mt-1" data-testid="support-message" />
          </div>

          <div>
            <Label className="text-xs">Pièce jointe (optionnel — max {MAX_MB} MB)</Label>
            {attachment ? (
              <div className="mt-1 flex items-center justify-between rounded-md border px-3 py-2 text-sm bg-muted/30">
                <div className="flex items-center gap-2 min-w-0">
                  <Paperclip className="h-4 w-4 text-primary shrink-0" />
                  <div className="truncate">{attachment.filename} <span className="text-muted-foreground">({Math.round(attachment.size / 1024)} Ko)</span></div>
                </div>
                <button type="button" onClick={() => setAttachment(null)} className="text-muted-foreground hover:text-rose-600" data-testid="support-remove-attachment">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="mt-1 flex items-center justify-center gap-2 rounded-md border border-dashed px-3 py-3 text-sm text-muted-foreground cursor-pointer hover:bg-muted/30">
                <Paperclip className="h-4 w-4" />
                <span>Choisir un fichier (capture, PDF, log…)</span>
                <input type="file" className="hidden" accept="image/*,application/pdf,text/plain,.log" onChange={handleFile} data-testid="support-attachment-input" />
              </label>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={closeSupport} className="rounded-full" data-testid="support-cancel">Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="btn-gradient rounded-full" data-testid="support-submit">
            {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Envoi…</> : "Envoyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
